import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { Gauge, Globe, Leaf, Zap } from "lucide-react";
import { useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getReporteInventario } from "@/lib/api";

const PRODUCTOS: { key: string; icon: LucideIcon; activo: string }[] = [
  { key: "Energía", icon: Zap, activo: "bg-amber-100 text-amber-700 border-transparent" },
  { key: "Potencia", icon: Gauge, activo: "bg-indigo-100 text-indigo-700 border-transparent" },
  { key: "CEL", icon: Leaf, activo: "bg-emerald-100 text-emerald-700 border-transparent" },
  { key: "i-REC", icon: Globe, activo: "bg-sky-100 text-sky-700 border-transparent" },
];

const AGRUPACIONES: { key: string; label: string }[] = [
  { key: "anio", label: "Año" },
  { key: "mes", label: "Mes" },
  { key: "dia", label: "Día" },
];

const HORIZONTES = [1, 3, 5, 10, 15, 30, 50];

const COLOR_INV = "#059669"; // esmeralda (línea de inventario)
const COLOR_LARGA = "#0284c7"; // azul cielo (posición larga, banda superior)
const COLOR_CORTA = "#dc2626"; // rojo (posición corta, banda inferior)
const COLOR_COMPRAS = "#4f46e5"; // índigo (barra simple de compras)
const COLOR_VENTAS = "#f59e0b"; // ámbar (barra simple de ventas)
// Paleta para colorear cada contraparte (consistente entre lados Compra/Venta).
const PALETA = [
  "#4f46e5", "#0ea5e9", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#22c55e", "#eab308", "#06b6d4", "#a855f7", "#f97316",
];

const nf = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 2 });
const fmtFecha = (iso: string) =>
  new Date(iso + "T00:00:00").toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

// Botón segmentado reutilizable.
function Seg({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-3 py-1.5 text-sm font-semibold border transition ${
        active
          ? "bg-slate-800 text-white border-slate-800"
          : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

export function ReporteInventarioPage() {
  const [producto, setProducto] = useState("Potencia");
  const [agrupacion, setAgrupacion] = useState("mes");
  const [horizonte, setHorizonte] = useState(5);
  const [incluirPasado, setIncluirPasado] = useState(false);
  const [apilar, setApilar] = useState(true);
  // Solo Energía: graficar MWh/h contratados vigentes (stock) en vez de la
  // energía acumulada del periodo (MWh = ee × horas).
  const [energiaAgrupada, setEnergiaAgrupada] = useState(false);
  const esEnergia = producto === "Energía";

  const q = useQuery({
    queryKey: [
      "reporte-inventario", producto, agrupacion, horizonte, incluirPasado,
      esEnergia && energiaAgrupada,
    ],
    queryFn: () =>
      getReporteInventario({
        producto, agrupacion, horizonte, incluirPasado,
        energiaAgrupada: esEnergia ? energiaAgrupada : false,
      }),
  });

  const data = q.data;
  const puntos = data?.puntos ?? [];
  const series = data?.series ?? [];
  const contratos = data?.contratos ?? [];
  const contrapartes = Array.from(new Set(series.map((s) => s.contraparte)));
  const colorDe = (cp: string) => PALETA[Math.max(0, contrapartes.indexOf(cp)) % PALETA.length];
  const totales = data?.totales;
  const unidad = data?.unidad ?? "";
  const unidadContrato = data?.unidad_contrato ?? unidad;
  const conPosicion = data?.con_posicion ?? false; // Energía, Potencia y CEL
  const agrupUsada = data?.agrupacion; // puede diferir de la pedida (auto-degradado)
  const degradado = agrupUsada && agrupUsada !== agrupacion;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Reporte de Inventario</h1>
        <p className="text-sm text-slate-500 mt-1">
          Compras y ventas contratadas por periodo (barras) e inventario = posición neta
          (compras − ventas) activa al cierre de cada periodo (línea).
        </p>
      </div>

      {/* ---- Controles ---- */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        {/* Producto */}
        <div>
          <div className="text-[11px] font-medium text-slate-500 mb-1">Producto</div>
          <div className="flex flex-wrap items-center gap-2">
            {PRODUCTOS.map((p) => {
              const Icon = p.icon;
              const active = producto === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setProducto(p.key)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1 rounded px-3 py-1.5 text-sm font-semibold border transition ${
                    active ? p.activo : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {p.key}
                </button>
              );
            })}
          </div>
        </div>

        {/* Agrupación */}
        <div>
          <div className="text-[11px] font-medium text-slate-500 mb-1">Agrupar por</div>
          <div className="inline-flex rounded overflow-hidden divide-x divide-slate-200 border border-slate-200">
            {AGRUPACIONES.map((a) => (
              <Seg key={a.key} active={agrupacion === a.key} onClick={() => setAgrupacion(a.key)}>
                {a.label}
              </Seg>
            ))}
          </div>
        </div>

        {/* Horizonte */}
        <div>
          <div className="text-[11px] font-medium text-slate-500 mb-1">Horizonte a futuro</div>
          <select
            value={horizonte}
            onChange={(e) => setHorizonte(Number(e.target.value))}
            className="border border-slate-200 rounded px-3 py-1.5 text-sm bg-white text-slate-700"
          >
            {HORIZONTES.map((h) => (
              <option key={h} value={h}>
                {h} {h === 1 ? "año" : "años"}
              </option>
            ))}
          </select>
        </div>

        {/* Incluir pasado */}
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none pb-1.5">
          <input
            type="checkbox"
            checked={incluirPasado}
            onChange={(e) => setIncluirPasado(e.target.checked)}
            className="w-4 h-4"
          />
          Incluir histórico (pasado)
        </label>

        {/* Apilar por contraparte */}
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none pb-1.5">
          <input
            type="checkbox"
            checked={apilar}
            onChange={(e) => setApilar(e.target.checked)}
            className="w-4 h-4"
          />
          Apilar por contraparte
        </label>

        {/* Solo Energía: MWh/h contratados en vez de energía acumulada */}
        {esEnergia && (
          <label
            className="inline-flex items-center gap-2 text-sm text-slate-600 select-none pb-1.5"
            title="Muestra los MWh/h contratados vigentes al cierre de cada periodo, en vez de la energía acumulada del periodo (MWh/h × horas)."
          >
            <input
              type="checkbox"
              checked={energiaAgrupada}
              onChange={(e) => setEnergiaAgrupada(e.target.checked)}
              className="w-4 h-4"
            />
            Agrupar la energía (MWh/h, no acumulada)
          </label>
        )}
      </div>

      {degradado && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Se ajustó la agrupación a <b>{AGRUPACIONES.find((a) => a.key === agrupUsada)?.label}</b> por
          el horizonte seleccionado (demasiados puntos para {AGRUPACIONES.find((a) => a.key === agrupacion)?.label}).
        </div>
      )}

      {/* ---- Gráfica ---- */}
      <div className="bg-white border rounded-lg p-4">
        {q.isLoading && <div className="p-8 text-center text-sm text-slate-500">Cargando…</div>}
        {q.isError && (
          <div className="p-8 text-center text-sm text-red-600">{(q.error as Error).message}</div>
        )}
        {!q.isLoading && !q.isError && puntos.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">
            No hay contratos de {producto} para graficar.
          </div>
        )}
        {puntos.length > 0 && (
          <>
            <div className="text-xs text-slate-500 mb-2">Unidad: {unidad}</div>
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer>
                {/* barSize fijo con muchos puntos: con >150 categorías el ancho auto
                    de recharts cae a 0px y las barras desaparecen (p. ej. 25-30 años × mes) */}
                <ComposedChart
                  data={puntos}
                  barGap={0}
                  barSize={puntos.length > 150 ? Math.max(1, Math.floor(1500 / puntos.length / 2)) : undefined}
                  margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={64} minTickGap={8} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    width={64}
                    tickFormatter={(v) => nf.format(v as number)}
                    label={{ value: unidad, angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#64748b" } }}
                  />
                  <Tooltip
                    formatter={(v: number, name: string) => [`${nf.format(v)} ${unidad}`, name]}
                    labelFormatter={(l) => `${l}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {apilar ? (
                    series.map((s) => (
                      <Bar
                        key={s.key}
                        dataKey={s.key}
                        stackId={s.tipo}
                        fill={colorDe(s.contraparte)}
                        name={`${s.tipo} · ${s.contraparte}`}
                      />
                    ))
                  ) : (
                    <>
                      <Bar dataKey="compras" name="Compras" fill={COLOR_COMPRAS} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="ventas" name="Ventas" fill={COLOR_VENTAS} radius={[3, 3, 0, 0]} />
                    </>
                  )}
                  <Line
                    type="monotone"
                    dataKey="inventario"
                    name="Inventario (neto)"
                    stroke={COLOR_INV}
                    strokeWidth={2.5}
                    dot={puntos.length <= 60 ? { r: 3 } : false}
                  />
                  {conPosicion && (
                    <Line
                      type="monotone"
                      dataKey="posicion_larga"
                      name="Límite Posición larga"
                      stroke={COLOR_LARGA}
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      dot={false}
                    />
                  )}
                  {conPosicion && (
                    <Line
                      type="monotone"
                      dataKey="posicion_corta"
                      name="Límite Posición corta"
                      stroke={COLOR_CORTA}
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      dot={false}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Inventario positivo = MPP largo (compró más de lo que vendió); negativo = corto.
              Volumen tomado del snapshot vigente más reciente de cada contrato.
              {conPosicion && (
                <>
                  {" "}Posición larga/corta = bandas sobre las compras (larga +15% &lt;1año / +20% ≥1año;
                  corta −5% &lt;1año / −10% ≥1año, según la duración de cada transacción).
                </>
              )}
            </p>
          </>
        )}
      </div>

      {/* ---- Tabla de contratos involucrados ---- */}
      {contratos.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-2 border-b bg-slate-50 text-sm font-semibold text-slate-700">
            Contratos de {producto} ({contratos.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="px-4 py-2 font-medium">Folio</th>
                  <th className="px-4 py-2 font-medium">Contraparte</th>
                  <th className="px-4 py-2 font-medium">Tipo</th>
                  <th className="px-4 py-2 font-medium text-right">Cantidad ({unidadContrato})</th>
                  <th className="px-4 py-2 font-medium text-right"
                    title="Volumen de toda la vigencia del contrato">
                    Acumulado ({data?.unidad_acumulado ?? unidadContrato})
                  </th>
                  <th className="px-4 py-2 font-medium">Disponibilidad</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {contratos.map((c) => (
                  <tr key={c.folio} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium text-slate-800">{c.folio}</td>
                    <td className="px-4 py-2 text-slate-600">{c.contraparte}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${
                          c.tipo === "Compra"
                            ? "bg-indigo-100 text-indigo-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {c.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-800">
                      {nf.format(c.cantidad)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-800">
                      {c.acumulado != null ? nf.format(c.acumulado) : "—"}
                    </td>
                    <td className="px-4 py-2 text-slate-500 text-xs">
                      {fmtFecha(c.vigencia_desde)} – {fmtFecha(c.vigencia_hasta)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {totales && (
                <tfoot>
                  <tr className="border-t bg-slate-50 font-semibold text-slate-800">
                    <td className="px-4 py-2" colSpan={3}>
                      Totales — Compras {nf.format(totales.compras)} · Ventas{" "}
                      {nf.format(totales.ventas)} · Neto {nf.format(totales.inventario)} {unidadContrato}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {nf.format(totales.compras + totales.ventas)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">
                      {totales.acumulado_compras != null && (
                        <>
                          <div className="text-indigo-700">
                            Compras {nf.format(totales.acumulado_compras)}
                          </div>
                          <div className="text-amber-700">
                            Ventas {nf.format(totales.acumulado_ventas ?? 0)}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-2" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
